import React, {Component, ReactElement, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import styled from 'styled-components'
import {Box, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, TextField} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {LeftRowFlexContainer, StretchRowFlexContainer} from '../../styledComponents/FlexComponents'
import {Colors, FontSizeInPixel, FontWeights} from '../../styledComponents/Constants'
import PPDatePicker from '../shared/forms/PPDatePicker'
import {Nullable} from '../../app/TypeHelpers'
import {BackLink} from '../../styledComponents/adminPage/Links'
import {AdminDefaultMediumButton, AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import AssocDirectorRepository, {RoomToResident} from './AssocDirectorRepository'
import PPSelect, {OptionProps} from '../shared/forms/PPSelect'
import {NatGetRidge, RidgeData} from '../../__NoAutomatedTests/components/ridge/repository/NatGetRidge'
import NatRidgeRepo from '../../__NoAutomatedTests/components/ridge/repository/NatRidgeRepo'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {isNull} from '../../__NoAutomatedTests/common/util/Natutil'
import PPTag from '../shared/forms/PPTag'
import {ResidentUsersSearchResult} from './model/ResidentUsersSearchResult'
import {ResidentUsersSearchConditions} from './model/ResidentUsersSearchConditions'
import {HKConstants} from '../../HKConstants'
import {RoomResidentInfo} from './model/RoomResidentInfo'
import {RoomResidentInfoForConfirmRegister} from './model/RoomResidentInfoForConfirmRegister'
import {AssocTerm} from './model/AssocTerm'
import {MESSAGE_TYPE, RESIDENT_STATUS} from '../../CodeMaster'
import {AssocDirectorHelper} from './utils/AssocDirectorHelper'
import {Warning} from '../../__NoAutomatedTests/components/common/admin/NatAcceptedWarning'
import {Information} from '../../network/response/Information'
import NatAdminCommonAcceptedWarningComponent
    from '../../__NoAutomatedTests/components/common/admin/NatAdminCommonAcceptedWarningComponent'
import PPErrorLabel from '../shared/forms/PPErrorLabel'
import {AssocOfCondominium} from './model/AssocOfCondominium'
import {SelectChangeEvent} from '@mui/material/Select'
import {Location} from 'react-router-dom'

export interface RegisterAssocDirectorLocationState {
    assocList: AssocOfCondominium[],
    selectedAssocId: number,
    selectedAssocTerm: object,
}

interface Props {
    ridgeRepo: NatRidgeRepo
    assocDirectorRepo: AssocDirectorRepository
    condominiumCd: string
    eventEmitter: EventEmitterExAdmin
    location: Location<RegisterAssocDirectorLocationState>
}

interface SearchedResidentUser extends RoomResidentInfo {
    isSelected: boolean
    isDisabled: boolean
}

interface State {
    targetAssocTerm: AssocTerm
    isLoadCompleted: boolean
    beforeSearch: boolean
    ridgeCd: string,
    roomNumber: string,
    familyName: string,
    firstName: string,
    startDate: Nullable<Date>
    endDate: Nullable<Date>
    limitOverFlag: boolean,
    searchedResidentUserList: SearchedResidentUser[]
    selectedResidentUserList: RoomResidentInfoForConfirmRegister[]
    ridgeList: RidgeData[]
    warnings: Warning[]
    isAcceptedAllWarningOrNothingWarn: boolean
}

interface Column {
    id: 'selectCheckBox' | 'roomNumber' | 'name' | 'adminName' | 'role' | 'mailAddress' | 'userType';
    label: string;
    width?: number;
    align?: 'inherit' | 'left' | 'center' | 'right' | 'justify';
}

const selectCellWidth = 70
const headerColumns: Column[] = [
    {id: 'selectCheckBox', label: '選択', align: 'center', width: selectCellWidth},
    {id: 'roomNumber', label: '部屋番号'},
    {id: 'name', label: '氏名'},
    {id: 'adminName', label: '管理者確認用氏名'},
    {id: 'role', label: '所有者区分'},
    {id: 'mailAddress', label: 'メールアドレス'},
    {id: 'userType', label: 'ユーザタイプ'},
]

class AdminRegisterAssocDirectorPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        const {
            assocList,
            selectedAssocId,
            selectedAssocTerm,
        } = props.location.state as RegisterAssocDirectorLocationState

        if (!assocList) {
            throw Error('管理組合リストが渡されていない')
        }

        if (assocList.length === 0) {
            throw Error('渡された管理組合リストの中身がない')
        }

        if (!selectedAssocId) {
            throw Error('処理対象の管理組合IDが渡されていない')
        }

        if (!selectedAssocTerm) {
            throw Error('処理対処の期が渡されていない')
        }

        const selectedAssoc = assocList.find(assoc => assoc.assocId === selectedAssocId)

        if (!selectedAssoc) {
            throw Error('選択された管理組合がリストにない')
        }

        const targetAssocTerm = AssocTerm.from(selectedAssocTerm)

        this.state = {
            targetAssocTerm: targetAssocTerm,
            isLoadCompleted: false,
            beforeSearch: true,
            ridgeCd: '',
            roomNumber: '',
            familyName: '',
            firstName: '',
            startDate: targetAssocTerm?.assocTermStartDate.toDate() ?? null,
            endDate: targetAssocTerm?.assocTermEndDate.toDate() ?? null,
            limitOverFlag: false,
            searchedResidentUserList: [],
            selectedResidentUserList: [],
            ridgeList: [],
            warnings: [],
            isAcceptedAllWarningOrNothingWarn: true,
        }

        this.handleChangeRidge = this.handleChangeRidge.bind(this)
        this.handleChangeRoomNumber = this.handleChangeRoomNumber.bind(this)
        this.handleChangeFamilyName = this.handleChangeFamilyName.bind(this)
        this.handleChangeFirstName = this.handleChangeFirstName.bind(this)
        this.handleKeyPress = this.handleKeyPress.bind(this)
        this.handleClickSearchResidentUsers = this.handleClickSearchResidentUsers.bind(this)
        this.handleCheckResidentUser = this.handleCheckResidentUser.bind(this)
        this.handleClickSelectedResidentUserClear = this.handleClickSelectedResidentUserClear.bind(this)
        this.handleChangeStartDate = this.handleChangeStartDate.bind(this)
        this.handleChangeEndDate = this.handleChangeEndDate.bind(this)
        this.handleClickPreviousPage = this.handleClickPreviousPage.bind(this)
        this.handleClickRegisterAssocDirector = this.handleClickRegisterAssocDirector.bind(this)
        this.handleChangeAssocDirectorResisterAcceptedWarning =
            this.handleChangeAssocDirectorResisterAcceptedWarning.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, ridgeRepo, eventEmitter} = this.props

        await eventEmitter.showLoading()
        try {
            // 棟取得
            const ridgeResponse: Nullable<NatGetRidge> = await this.props.asyncCallFunction(
                ridgeRepo.getRidgeList.bind(ridgeRepo, condominiumCd),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                })
            if (ridgeResponse === null || ridgeResponse.details === null) {
                return
            }
            this.setState({
                isLoadCompleted: true,
                ridgeList: ridgeResponse.details,
            })
        } finally {
            await eventEmitter.closeLoading()
        }
    }

    handleChangeRidge(event: SelectChangeEvent<unknown>): void {
        this.setState({
            ridgeCd: String(event.target.value),
        })
    }

    handleChangeRoomNumber(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            roomNumber: event.target.value,
        })
    }

    handleChangeFamilyName(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            familyName: event.target.value,
        })
    }

    handleChangeFirstName(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            firstName: event.target.value,
        })
    }

    handleKeyPress(event: React.KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.handleClickSearchResidentUsers()
        }
    }

    async handleClickSearchResidentUsers(): Promise<void> {
        const {assocDirectorRepo, condominiumCd, eventEmitter, location, asyncCallFunction} = this.props
        const {ridgeCd, roomNumber, familyName, firstName, selectedResidentUserList} = this.state

        await eventEmitter.showLoading()
        try {
            const searchConditions: ResidentUsersSearchConditions = {
                ridgeCd: isNull(ridgeCd),
                roomNumber: isNull(roomNumber),
                familyName: isNull(familyName),
                firstName: isNull(firstName),
            }
            const residentUserSearchResult: Nullable<ResidentUsersSearchResult> = await asyncCallFunction(
                assocDirectorRepo.searchResidentUsers.bind(
                    assocDirectorRepo,
                    condominiumCd,
                    location.state.selectedAssocId,
                    searchConditions,
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )
            if (residentUserSearchResult === null) {
                return
            }
            let searchedResidentUserList: SearchedResidentUser[] = []

            if (!residentUserSearchResult.limitOverFlag) {
                searchedResidentUserList = residentUserSearchResult.roomResidentInfoList
                    .map(roomResidentInfo => {
                        const isSelected = selectedResidentUserList.some(selectedResidentUser =>
                            roomResidentInfo.sutekiNetId === selectedResidentUser.sutekiNetId,
                        )
                        const isDisabled = selectedResidentUserList.some(selectedResidentUser =>
                            roomResidentInfo.sutekiNetId === selectedResidentUser.sutekiNetId
                            && (roomResidentInfo.ridgeCd !== selectedResidentUser.ridgeCd
                                || roomResidentInfo.roomNumber !== selectedResidentUser.roomNumber),
                        )
                        return ({
                            ...roomResidentInfo,
                            isSelected: isSelected,
                            isDisabled: isDisabled,
                        })
                    })
            }

            this.setState({
                beforeSearch: false,
                limitOverFlag: residentUserSearchResult.limitOverFlag,
                searchedResidentUserList: searchedResidentUserList,
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleCheckResidentUser(
        checked: boolean,
        sutekiNetId: string,
        ridgeCd: string,
        ridgeName: string,
        roomNumber: string,
    ): void {
        if (checked) {
            this.selectResidentUser(sutekiNetId, ridgeCd, ridgeName, roomNumber)
        } else {
            this.unselectResidentUser(sutekiNetId)
        }
    }

    handleClickSelectedResidentUserClear(sutekiNetId: string): void {
        this.unselectResidentUser(sutekiNetId)
    }

    private selectResidentUser(sutekiNetId: string, ridgeCd: string, ridgeName: string, roomNumber: string): void {
        const {searchedResidentUserList, selectedResidentUserList} = this.state

        searchedResidentUserList
            .filter(searchedResidentUser => searchedResidentUser.sutekiNetId === sutekiNetId)
            .forEach(searchedResidentUser => {
                    searchedResidentUser.isSelected = true
                    if (searchedResidentUser.ridgeCd === ridgeCd
                        && searchedResidentUser.ridgeName === ridgeName
                        && searchedResidentUser.roomNumber === roomNumber) {
                        selectedResidentUserList.push({
                            ridgeCd: searchedResidentUser.ridgeCd,
                            roomNumber: searchedResidentUser.roomNumber,
                            ridgeName: searchedResidentUser.ridgeName,
                            roleName: searchedResidentUser.roleName,
                            adminFamilyName: searchedResidentUser.adminFamilyName,
                            adminFirstName: searchedResidentUser.adminFirstName,
                            status: searchedResidentUser.status,
                            sutekiNetId: searchedResidentUser.sutekiNetId,
                            familyName: searchedResidentUser.familyName,
                            firstName: searchedResidentUser.firstName,
                        })
                    } else {
                        searchedResidentUser.isDisabled = true
                    }
                },
            )

        this.setState({
            searchedResidentUserList,
            selectedResidentUserList,
        })
    }

    private unselectResidentUser(sutekiNetId: string): void {
        const {searchedResidentUserList} = this.state
        let {selectedResidentUserList} = this.state

        searchedResidentUserList
            .filter(searchedResidentUser => searchedResidentUser.sutekiNetId === sutekiNetId)
            .forEach(searchedResidentUser => {
                searchedResidentUser.isSelected = false
                searchedResidentUser.isDisabled = false

            })

        selectedResidentUserList = selectedResidentUserList
            .filter(selectedResidentUser => selectedResidentUser.sutekiNetId !== sutekiNetId)

        this.setState({
            searchedResidentUserList,
            selectedResidentUserList,
        })
    }

    handleChangeStartDate(date: Nullable<Date>): void {
        this.setState({
            startDate: date,
        })
    }

    handleChangeEndDate(date: Nullable<Date>): void {
        this.setState({
            endDate: date,
        })
    }

    handleClickPreviousPage(): void {
        this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/assoc-directors`, {replace: true})
    }

    async handleClickRegisterAssocDirector(): Promise<void> {
        const {
            navigate,
            condominiumCd,
            assocDirectorRepo,
            asyncCallFunction,
            eventEmitter,
            location,
        } = this.props
        const {targetAssocTerm, startDate, endDate, selectedResidentUserList, warnings} = this.state

        eventEmitter.clearBroadcastedErrors()
        await eventEmitter.showLoading()
        try {
            if (startDate === null || endDate === null) {
                return
            }

            const registerRoomToResidentInfoList = selectedResidentUserList.map(
                selectedResidentUser => {
                    const registerRoomToResidentInfo: RoomToResident = {
                        ridgeCd: selectedResidentUser.ridgeCd,
                        roomNumber: selectedResidentUser.roomNumber,
                        sutekiNetId: selectedResidentUser.sutekiNetId,
                    }
                    return registerRoomToResidentInfo
                },
            )

            const assocTermId: number = targetAssocTerm.assocTermId

            const registerAssocDirectorsResponse: Nullable<Information[]> = await asyncCallFunction(
                // @ts-ignore
                assocDirectorRepo.registerAssocDirectors.bind(
                    assocDirectorRepo,
                    condominiumCd,
                    location.state.selectedAssocId,
                    assocTermId,
                    startDate,
                    endDate,
                    registerRoomToResidentInfoList,
                    warnings.map(warn => ({messageId: warn.messageId})),
                ),
                (httpErrorOrNetworkError) => {
                    httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )
            if (registerAssocDirectorsResponse === null) {
                return
            }

            const firstInformation = registerAssocDirectorsResponse[0]

            if (firstInformation.type === MESSAGE_TYPE.WARNING) {
                this.setState({
                    warnings: registerAssocDirectorsResponse.map(warn => ({
                        messageId: warn.messageId,
                        message: warn.message,
                    })),
                    isAcceptedAllWarningOrNothingWarn: false,
                })

                return
            }

            navigate(
                `/admin/condominiums/${condominiumCd}/assoc-directors`,
                {
                    state: {
                        information: firstInformation,
                    },
                },
            )

        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleChangeAssocDirectorResisterAcceptedWarning = (acceptAll: boolean) => {
        this.setState({isAcceptedAllWarningOrNothingWarn: acceptAll})
    }

    renderAssocTermInfo(
        assocList: AssocOfCondominium[],
        selectedAssocId: number,
        selectedAssocTerm: AssocTerm,
    ): ReactNode {
        let selectedAssoc: AssocOfCondominium | undefined
        if (assocList.length >= 2) {
            selectedAssoc = assocList.find(assoc => assoc.assocId === selectedAssocId)
            if (!selectedAssoc) {
                throw Error('管理組合のリストに選択された管理組合の情報がない')
            }
        }
        return <>
            <Box height={9}/>
            {selectedAssoc &&
                <>
                    <LeftRowFlexContainer>
                        <AssocItemName>管理組合</AssocItemName>
                        <AssocName>{selectedAssoc.assocName}</AssocName>
                    </LeftRowFlexContainer>
                    <Box height={'8px'}/>
                </>
            }
            <LeftRowFlexContainer>
                <AssocTermItemName>理事会の任期</AssocTermItemName>
                <AssocTermName>{AssocDirectorHelper.getDisplayAssocTerm(selectedAssocTerm)}</AssocTermName>
            </LeftRowFlexContainer>
        </>
    }

    render(): ReactNode {
        const {
            eventEmitter,
            location,
        } = this.props

        const {
            targetAssocTerm,
            isLoadCompleted,
            beforeSearch,
            ridgeCd,
            startDate,
            endDate,
            limitOverFlag,
            searchedResidentUserList,
            selectedResidentUserList,
            ridgeList,
            warnings,
            isAcceptedAllWarningOrNothingWarn,
        } = this.state
        const registerButtonDisabled = !startDate || !endDate
            || selectedResidentUserList.length === 0 || !isAcceptedAllWarningOrNothingWarn

        if (!isLoadCompleted) {
            return null
        }

        const existRidge = ridgeList[0].ridgeName !== '' || ridgeList.length > 1
        let ridgeSelectList: OptionProps[] = []
        if (existRidge) {
            ridgeSelectList = ridgeList.map(ridge => ({
                label: `${ridge.ridgeCd}：${ridge.ridgeName}`,
                value: `${ridge.ridgeCd}`,
            }))
            ridgeSelectList.unshift(
                {
                    label: '指定なし',
                    value: '',
                },
            )
        }

        return <>
            {this.renderAssocTermInfo(location.state.assocList, location.state.selectedAssocId, targetAssocTerm)}
            <Box height={24}/>
            居住者ユーザを選択してください。
            <WhiteArea>
                <SearchFormArea>
                    {existRidge && <>
                        棟名
                        <Box width={8}/>
                        <RidgeSelect
                            fullWidth
                            data={ridgeSelectList}
                            value={ridgeCd}
                            onChange={this.handleChangeRidge}
                            onKeyPress={this.handleKeyPress}
                        />
                        <Box width={48} flexShrink={4}/>
                    </>}
                    <label>
                        <LeftRowFlexContainer>
                            部屋番号
                            <Box width={8}/>
                            <RoomNumberTextField
                                value={this.state.roomNumber}
                                variant={'standard'}
                                InputProps={{
                                    inputProps: {
                                        maxLength: 4,
                                    },
                                }}
                                onChange={this.handleChangeRoomNumber}
                                onKeyPress={this.handleKeyPress}
                                autoFocus
                            />
                        </LeftRowFlexContainer>
                    </label>
                    <Box width={48} flexShrink={4}/>
                    <label>
                        <LeftRowFlexContainer>
                            氏名(姓)
                            <Box width={8}/>
                            <NameTextField
                                value={this.state.familyName}
                                variant={'standard'}
                                InputProps={{
                                    inputProps: {
                                        maxLength: 10,
                                    },
                                }}
                                onChange={this.handleChangeFamilyName}
                                onKeyPress={this.handleKeyPress}
                            />
                        </LeftRowFlexContainer>
                    </label>
                    <Box width={48} flexShrink={4}/>
                    <label>
                        <LeftRowFlexContainer>
                            氏名(名)
                            <Box width={8}/>
                            <NameTextField
                                value={this.state.firstName}
                                variant={'standard'}
                                InputProps={{
                                    inputProps: {
                                        maxLength: 10,
                                    },
                                }}
                                onChange={this.handleChangeFirstName}
                                onKeyPress={this.handleKeyPress}
                            />
                        </LeftRowFlexContainer>
                    </label>
                    <Box width={24}/>
                    <AdminDefaultMediumButton
                        onClick={this.handleClickSearchResidentUsers}
                    >
                        居住者ユーザを検索する
                    </AdminDefaultMediumButton>
                </SearchFormArea>
                <Box mt={'16px'} mb={'8px'} fontWeight={'bold'}>
                    検索結果({limitOverFlag ?
                    HKConstants.VMN.RES.RESIDENCE_USER_SEARCH_RESULTS_LIMIT + '+' : searchedResidentUserList.length})
                </Box>
                <ResidentUserTableArea>
                    <Table stickyHeader size={'small'}>
                        <TableHead>
                            <TableRow>
                                {headerColumns.map(
                                    headerColumn =>
                                        <ResidentUserTableHeaderCell
                                            key={`header${headerColumn.id}`}
                                            align={headerColumn.align}
                                            style={{
                                                width: headerColumn.width,
                                            }}
                                        >
                                            {headerColumn.label}
                                        </ResidentUserTableHeaderCell>,
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                !beforeSearch &&
                                searchedResidentUserList.map((residentUser, index) =>
                                    this.renderRegisterUserTableRow(index, residentUser))
                            }
                        </TableBody>
                    </Table>
                    {/* 検索結果が一行しかない場合にセルの縦幅がテーブルの縦幅になる問題に対応できないため、テーブル外で下記を表示*/}
                    {beforeSearch &&
                        <TableTextArea>
                            検索結果が表示されます。
                        </TableTextArea>
                    }
                    {limitOverFlag &&
                        <TableTextArea>
                            <div>検索結果が{HKConstants.VMN.RES.RESIDENCE_USER_SEARCH_RESULTS_LIMIT}件を超えています。</div>
                            <div>
                                件数が{HKConstants.VMN.RES.RESIDENCE_USER_SEARCH_RESULTS_LIMIT}
                                件以下になるように検索条件を変更し、再度検索してください。
                            </div>
                        </TableTextArea>
                    }
                </ResidentUserTableArea>
                <Box mt={'16px'} mb={'8px'} fontWeight={'bold'}>
                    選択中の居住者ユーザ({selectedResidentUserList.length})
                </Box>
                <SelectedResidentUserArea>
                    {selectedResidentUserList.map(residentUser => {
                        const roomNumber = `${residentUser.ridgeName} ${residentUser.roomNumber}`.trim()
                        const familyName = residentUser.familyName ?? ''
                        const firstName = residentUser.firstName ?? ''
                        let name = `${familyName} ${firstName}`.trim()
                        if (residentUser.status === RESIDENT_STATUS.UNREGISTERED) {
                            name = '（未登録）'
                        } else if (name === '') {
                            name = '（退会済）'
                        }
                        const text = `${roomNumber}　${name}`
                        return (
                            <TagWrapper key={`tagWrapper${residentUser.sutekiNetId}`}>
                                <PPTag
                                    key={`selectedResidentUser${residentUser.sutekiNetId}`}
                                    text={text}
                                    onClickClearButton={() =>
                                        this.handleClickSelectedResidentUserClear(residentUser.sutekiNetId)
                                    }
                                    eventEmitter={eventEmitter}
                                    eventKeys={[`selectedResidentUserTag${residentUser.sutekiNetId}`]}
                                />
                            </TagWrapper>
                        )
                    })}
                </SelectedResidentUserArea>
            </WhiteArea>
            役員向け機能の利用期間を設定してください。
            <WhiteArea>
                <TermInputFormArea id={'term-input-form-area'}>
                    <DateLabel style={{paddingTop: 8.2}}>
                        <PPDatePicker
                            value={startDate}
                            onChangeDate={this.handleChangeStartDate}
                            maxDate={endDate ?? undefined}
                        />
                        <Box width={26}/>
                        <Box mt={'12px'}>〜</Box>
                    </DateLabel>
                    <Box width={26}/>
                    <DateLabel style={{paddingTop: 8.2}}>
                        <PPDatePicker
                            value={endDate}
                            onChangeDate={this.handleChangeEndDate}
                            minDate={startDate ?? undefined}
                        />
                    </DateLabel>
                </TermInputFormArea>
                <PPErrorLabel
                    eventEmitter={eventEmitter}
                    eventKeys={['availableTermStartDate', 'availableTermEndDate']}
                />
            </WhiteArea>
            <NatAdminCommonAcceptedWarningComponent
                warnings={warnings}
                description={'内容をご確認いただき、問題なければチェックしたうえで再度役員を登録するボタンを押してください。'}
                changeAcceptedWarnings={this.handleChangeAssocDirectorResisterAcceptedWarning}
                doOpenAnimation={true}
            />
            <StretchRowFlexContainer>
                <BackLink onClick={this.handleClickPreviousPage}>
                    役員一覧に戻る
                </BackLink>
                <AdminPrimaryButton
                    onClick={this.handleClickRegisterAssocDirector}
                    disabled={registerButtonDisabled}
                >
                    役員を登録する
                </AdminPrimaryButton>
            </StretchRowFlexContainer>
        </>
    }

    renderRegisterUserTableRow(index: number, residentUser: SearchedResidentUser): ReactElement {
        let backgroundColor: string = Colors.Surface
        if (residentUser.isSelected) {
            backgroundColor = '#DEEBFF'
        }
        if (residentUser.isDisabled) {
            backgroundColor = '#F1F1F1'
        }
        const familyName = residentUser.familyName ?? ''
        const firstName = residentUser.firstName ?? ''
        let name = `${familyName} ${firstName}`.trim()
        if (residentUser.status === RESIDENT_STATUS.UNREGISTERED) {
            name = '（未登録）'
        } else if (name === '') {
            name = '（退会済）'
        }
        const adminFamilyName = residentUser.adminFamilyName ?? ''
        const adminFirstName = residentUser.adminFirstName ?? ''
        const adminName = `${adminFamilyName} ${adminFirstName}`.trim()
        const mailAddress = residentUser.mailAddress ?? ''

        return <TableRow key={`searchedResidentUser${index}`} style={{backgroundColor: backgroundColor}}>
            <ResidentUserTableSelectCell align={'center'} style={{width: selectCellWidth}}>
                <Checkbox
                    key={`searchResidentUserCheck${index}`}
                    color="primary"
                    checked={residentUser.isSelected}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleCheckResidentUser(
                        event.target.checked,
                        residentUser.sutekiNetId,
                        residentUser.ridgeCd,
                        residentUser.ridgeName,
                        residentUser.roomNumber,
                    )}
                    disabled={residentUser.isDisabled}
                />
            </ResidentUserTableSelectCell>
            <ResidentUserTableCell>
                {`${residentUser.ridgeName} ${residentUser.roomNumber}`.trim()}
            </ResidentUserTableCell>
            <ResidentUserTableCell>
                {name}
            </ResidentUserTableCell>
            <ResidentUserTableCell>
                {adminName}
            </ResidentUserTableCell>
            <ResidentUserTableCell>
                {residentUser.roleName}
            </ResidentUserTableCell>
            <ResidentUserTableCell>
                {mailAddress}
            </ResidentUserTableCell>
            <ResidentUserTableCell>
                {residentUser.residentUserType.label}
            </ResidentUserTableCell>
        </TableRow>
    }
}

export default createAdminPage(AdminRegisterAssocDirectorPage)

const WhiteArea = styled.div`
  font-size: 0.875rem;
  background: ${Colors.Surface};
  margin: 8px 0 16px;
  padding: 19px 24px 16px;
`

const SearchFormArea = styled(LeftRowFlexContainer)`
  white-space: nowrap;
  word-break: keep-all;

  .MuiButton-root {
    min-width: fit-content;
  }
`

const ResidentUserTableArea = styled.div`
  height: 208px;
  overflow: auto;
  border: ${Colors.AccentLight} solid 1px;
  white-space: nowrap;
  word-break: keep-all;

  .MuiTableCell-stickyHeader {
    padding: 6px 24px 6px 16px;
    left: unset;
  }
`

const ResidentUserTableHeaderCell = withStyles({
    root: {
        backgroundColor: '#fcfcfc',
    },
})(TableCell)

const ResidentUserTableCell = withStyles({
    root: {
        border: 'none',
        paddingTop: 0,
        paddingBottom: 0,
    },
})(TableCell)

const ResidentUserTableSelectCell = withStyles({
    root: {
        width: selectCellWidth,
    },
})(ResidentUserTableCell)

const TableTextArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 80%;
  justify-content: center;
`

const SelectedResidentUserArea = styled.div`
  min-height: 69px;
  border: ${Colors.AccentLight} solid 1px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  padding: 8px;
`

const TagWrapper = styled.div`
  margin: 4px;
`

const DateLabel = styled.label`
  display: flex;
  align-items: flex-start;
`

const TermInputFormArea = styled(LeftRowFlexContainer)`
  align-items: flex-start;
`

const RidgeSelect = styled(PPSelect)`
  max-width: 200px;
`

const RoomNumberTextField = styled(TextField)`
  max-width: 100px;
`

const NameTextField = styled(TextField)`
  max-width: 120px;
`

const AssocName = styled.div`
  font-size: ${FontSizeInPixel.Body}px;
  font-weight: ${FontWeights.Bold};
  overflow-wrap: anywhere;
  line-break: anywhere;
`
const AssocTermName = styled.div`
  font-size: ${FontSizeInPixel.Body}px;
  font-weight: ${FontWeights.Bold};
`

const AssocItemName = styled.div`
  min-width: 124px;
`

const AssocTermItemName = styled.div`
  min-width: 124px;
`
